import React, { useState, useEffect } from "react";
import { Modal, message } from "antd";
import { get } from "../services/ApiService";
import "./OrderDispatch.css";

const countStats = (stats) =>
  Object.values(stats || {}).reduce(
    (totals, stat) => {
      totals.created += Number(stat?.created || 0);
      totals.failed += Number(stat?.failed || 0);
      return totals;
    },
    { created: 0, failed: 0 }
  );

const getDispatchCounts = (responseData) =>
  ["local", "external"].reduce(
    (totals, scope) => {
      const scopedResult = responseData?.[scope];
      const royalMailTotals = countStats(scopedResult?.royalMail?.stats);
      const amazonTotals = countStats(scopedResult?.amazon?.stats);

      totals.created += royalMailTotals.created + amazonTotals.created;
      totals.failed += royalMailTotals.failed + amazonTotals.failed;
      return totals;
    },
    { created: 0, failed: 0 }
  );

const OrderDispatch = ({ openJobsInNewTab = true }) => {
  const [isSending, setIsSending] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [dispatching, setDispatching] = useState(false);

  // Call API on mount to check if dispatching is already in progress
  useEffect(() => {
    const checkLockStatus = async () => {
      try {
        const res = await get("api/orders/courier/lock");
        if (res.success && res.isLocked) {
          setDispatching(true);
          // message.warning("Dispatching is already in progress!");
        }
      } catch (error) {
        console.error("Error checking lock status:", error);
      }
    };

    checkLockStatus();
  }, []);

  const handleOpenModal = () => {
    if (dispatching) return; // prevent reopening while dispatching
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleOrdersPrepare = async () => {
    setIsSending(true);
    handleCloseModal(); // close modal immediately
    setDispatching(true); // show dispatching state
    message.loading({
      content: "Dispatching orders to courier...",
      key: "dispatchStatus",
    });

    if (openJobsInNewTab) {
      window.open("/orders/courier-jobs", "_blank");
    }

    try {
      const res = await get('api/orders/prepare/courier');
      const dispatchCounts = getDispatchCounts(res);

      if (!res?.success) {
        if (dispatchCounts.created > 0 && dispatchCounts.failed > 0) {
          message.warning({
            content: `Courier dispatch completed with some failures. ${dispatchCounts.created} succeeded, ${dispatchCounts.failed} failed. Check Courier Jobs for details.`,
            key: "dispatchStatus",
            duration: 5,
          });
          return;
        }

        throw new Error(
          res?.message || res?.error || "Courier dispatch failed."
        );
      }

      message.success({
        content:
          res?.message ||
          "Orders successfully sent to courier!",
        key: "dispatchStatus",
        duration: 3,
      });
    } catch (error) {
      console.error(error);
      message.error({
        content:
          error?.message ||
          "Error processing orders for courier.",
        key: "dispatchStatus",
        duration: 3,
      });
    } finally {
      setIsSending(false);
      setDispatching(false);
    }
  };

  return (
    <div>
      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
        <button
          className="btn btn-primary btn-sm"
          onClick={handleOpenModal}
          disabled={dispatching}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            fontWeight: 600,
            opacity: dispatching ? 0.8 : 1,
            backgroundColor: dispatching ? "#6c757d" : "",
            borderColor: dispatching ? "#6c757d" : "",
            cursor: dispatching ? "not-allowed" : "pointer",
          }}
        >
          {dispatching ? (
            <>
              <i className="fa fa-spinner fa-spin"></i>
              Dispatching in progress...
            </>
          ) : (
            <>
              SEND TO COURIER (BULK)
              <i className="fa fa-fighter-jet"></i>
            </>
          )}
        </button>
      </div>

      <Modal
        open={openModal}
        onCancel={handleCloseModal}
        footer={null}
        centered
        closeIcon={null}
        width={460}
        className="od-modal"
      >
        <div className="od-confirm">
          <div className="od-confirm-icon">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M22 2L11 13" />
              <path d="M22 2l-7 20-4-9-9-4z" />
            </svg>
          </div>
          <h4>Send orders to courier?</h4>
          <p>This will process and send every pending order from both locations to the courier. Labels will be created and the batch will appear in Courier jobs.</p>

          <div className="od-confirm-warning">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 3l10 18H2z" />
              <path d="M12 10v4" />
              <path d="M12 17h.01" />
            </svg>
            <span>This action cannot be undone. Orders are sent to the courier straight away.</span>
          </div>

          <div className="od-confirm-actions">
            <button
              type="button"
              className="od-btn"
              onClick={handleCloseModal}
              disabled={isSending}
            >
              Cancel
            </button>
            <button
              type="button"
              className="od-btn od-btn-primary"
              onClick={handleOrdersPrepare}
              disabled={isSending}
            >
              {isSending ? (
                <span>Sending…</span>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M22 2L11 13" />
                    <path d="M22 2l-7 20-4-9-9-4z" />
                  </svg>
                  <span>Send orders</span>
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default OrderDispatch;
